'use es6';

import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import PropTypes from 'prop-types';
import { Component } from 'react';
import styled from 'styled-components';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UISelect from 'UIComponents/input/UISelect';
import UITextInput from 'UIComponents/input/UITextInput';
import UIInputStaticLabel from 'UIComponents/input/UIInputStaticLabel';
import UICheckbox from 'UIComponents/input/UICheckbox';
import UIButton from 'UIComponents/button/UIButton';
import UIFileAttachment from 'UIComponents/attachment/UIFileAttachment';
import UILink from 'UIComponents/link/UILink';
import Small from 'UIComponents/elements/Small';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { isLikelyUrlWithoutProtocol } from '../../utils/url';
import { ExternalLinkTypes, LinkTypes, ReadOnlyLinkTypes } from '../../constants/link';
import StyleProtectedSelectItem from '../style-protected/StyleProtectedSelectItem';
import PageLinkField from './PageLinkField';
import MeetingLinkField from './MeetingLinkField';
import requiredBy from '../../utils/requiredBy';
import AnchorLinkField from './AnchorLinkField';
import { deserializeAnchorFromLink, serializeAnchorAsLink } from '../../utils/anchor';
import { alphabeticalSort } from '../../utils/link';
import FormLabelWithHelpLink from '../FormLabelWithHelpLink';
import EmailSubscriptionLinkField from './EmailSubscriptionLinkField';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = suffix => `RichTextLib.links.${suffix}`;
const UrlPreview = styled(Small).withConfig({
  displayName: "LinkFields__UrlPreview",
  componentId: "cvskby-0"
})(["float:right;padding-top:12px;"]);
export default class LinkFields extends Component {
  constructor(...args) {
    super(...args);
    this.handleLinkTypeChange = ({
      target: {
        value
      }
    }) => {
      this.props.onLinkTypeChange(value);
    };
    this.handleFileSelection = () => {
      const {
        onSelectFile,
        onUpdate
      } = this.props;
      onSelectFile(file => {
        onUpdate({
          href: file.url,
          linkDisplayName: `${file.name}.${file.extension}`,
          fileMeta: {
            fileName: `${file.name}.${file.extension}`,
            size: file.size,
            type: file.type
          }
        });
      });
    };
  }
  renderUrlSearchInput() {
    const {
      href,
      onUpdate,
      linkType,
      shouldAutoFocus,
      isValidLink
    } = this.props;
    const UrlFormControl = /*#__PURE__*/_jsx(UIFormControl, {
      label: I18n.text(i18nKey(`linkTypes.${LinkTypes.URL}.label`)),
      className: "m-top-0",
      error: !isValidLink,
      validationMessage: isValidLink ? '' : I18n.text(i18nKey('invalidLinkMessage')),
      children: /*#__PURE__*/_jsx(UITextInput, {
        autoFocus: shouldAutoFocus,
        "data-test-id": "url-search-input",
        value: linkType === LinkTypes.URL ? href : '',
        onKeyDown: this.props.onUrlKeyDown,
        onChange: ({
          target: {
            value
          }
        }) => {
          // may want to save protocol appending for onRemove so it doesn't just suddenly add it while typing
          const newHref = isLikelyUrlWithoutProtocol(value) ? `http://${value}` : value;
          onUpdate({
            href: newHref,
            linkDisplayName: newHref
          });
        }
      })
    });
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(UrlPreview, {
        children: /*#__PURE__*/_jsx(UILink, {
          external: true,
          href: href,
          disabled: !href,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey(`linkTypes.${LinkTypes.URL}.preview`)
          })
        })
      }), UrlFormControl]
    });
  }
  renderEmailInput() {
    const {
      href,
      onUpdate,
      shouldAutoFocus,
      isValidLink
    } = this.props;
    return /*#__PURE__*/_jsx(UIFormControl, {
      label: I18n.text(i18nKey(`linkTypes.${LinkTypes.EMAIL}.label`)),
      className: "m-top-0",
      error: !isValidLink,
      validationMessage: isValidLink ? '' : I18n.text(i18nKey('invalidLinkMessage')),
      children: /*#__PURE__*/_jsx(UIInputStaticLabel, {
        text: 'mailto:',
        position: "start",
        children: /*#__PURE__*/_jsx(UITextInput, {
          autoFocus: shouldAutoFocus,
          onKeyDown: this.props.onUrlKeyDown,
          "data-test-id": "email-address-input",
          value: href.replace(/^mailto:/, ''),
          onChange: ({
            target: {
              value
            }
          }) => {
            const newValue = value.replace(/^mailto:/, '');
            const newHref = `mailto:${newValue}`;
            onUpdate({
              href: newHref,
              linkDisplayName: newValue
            });
          }
        })
      })
    });
  }
  renderPageSelection() {
    const {
      href,
      onUpdate,
      linkType,
      dropdownClassName,
      fetchPages,
      shouldAutoFocus
    } = this.props;
    return /*#__PURE__*/_jsx(PageLinkField, {
      autoFocus: shouldAutoFocus,
      url: linkType === LinkTypes.PAGE ? href : '',
      fetchPages: fetchPages,
      dropdownClassName: dropdownClassName,
      onChange: ({
        url,
        pageName
      }) => {
        onUpdate({
          href: url,
          linkDisplayName: pageName
        });
      }
    });
  }
  renderFileSelection() {
    const {
      currentFileMeta
    } = this.props;
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(FormLabelWithHelpLink, {
        primaryText: i18nKey(`linkTypes.${LinkTypes.FILE}.label`),
        linkText: currentFileMeta ? i18nKey(`linkTypes.${LinkTypes.FILE}.replace`) : null,
        onLinkClick: this.handleFileSelection,
        "data-test-id": "link-file-replace"
      }), currentFileMeta ? /*#__PURE__*/_jsx(UIFileAttachment, {
        className: "hs-link-field--file-attachment",
        size: "small",
        onCloseClick: () => this.props.onUpdate({
          fileMeta: null,
          href: ''
        }),
        fileName: currentFileMeta.fileName,
        fileInfo: I18n.formatSize(currentFileMeta.size),
        fileType: currentFileMeta.type,
        closeable: true
      }) : /*#__PURE__*/_jsx(UIButton, {
        use: "tertiary-light",
        onClick: this.handleFileSelection,
        size: "small",
        "data-test-id": "link-select-file",
        children: I18n.text(i18nKey('selectFile'))
      })]
    });
  }
  renderEmailSubscriptionSelection() {
    const {
      href,
      onUpdate,
      linkType,
      dropdownClassName,
      emailSubscriptionLinkTypes
    } = this.props;
    return /*#__PURE__*/_jsx(EmailSubscriptionLinkField, {
      onUpdate: onUpdate,
      href: href,
      linkType: linkType,
      dropdownClassName: dropdownClassName,
      emailSubscriptionLinkTypes: emailSubscriptionLinkTypes
    });
  }
  renderMeetingSelection() {
    const {
      userId,
      href,
      fetchMeetingsByUsers,
      dropdownClassName,
      onUpdate
    } = this.props;
    return /*#__PURE__*/_jsx(MeetingLinkField, {
      userId: userId,
      url: href,
      fetchMeetingsByUsers: fetchMeetingsByUsers,
      dropdownClassName: dropdownClassName,
      onChange: ({
        url,
        meetingName
      }) => onUpdate({
        href: url,
        linkDisplayName: meetingName
      })
    });
  }
  renderAnchorInput() {
    const {
      href,
      fetchAnchors,
      onUpdate,
      dropdownClassName
    } = this.props;
    return /*#__PURE__*/_jsx(AnchorLinkField, {
      anchor: deserializeAnchorFromLink(href),
      fetchAnchors: fetchAnchors,
      dropdownClassName: dropdownClassName,
      onChange: ({
        anchorName
      }) => {
        onUpdate({
          href: serializeAnchorAsLink(anchorName),
          linkDisplayName: anchorName
        });
      }
    });
  }
  renderNoLinkInput() {
    return /*#__PURE__*/_jsx(UIFormControl, {
      label: I18n.text(i18nKey('linkTypes.no_link.label')),
      className: "m-top-0",
      children: /*#__PURE__*/_jsx(UITextInput, {
        disabled: true,
        value: I18n.text(i18nKey('linkTypes.no_link.placeholder'))
      })
    });
  }
  renderUrlField() {
    const {
      linkType
    } = this.props;
    switch (linkType) {
      case LinkTypes.URL:
        return this.renderUrlSearchInput();
      case LinkTypes.PAGE:
        return this.renderPageSelection();
      case LinkTypes.EMAIL:
        return this.renderEmailInput();
      case LinkTypes.FILE:
        return this.renderFileSelection();
      case LinkTypes.EMAIL_SUBSCRIPTION:
        return this.renderEmailSubscriptionSelection();
      case LinkTypes.MEETING:
        return this.renderMeetingSelection();
      case LinkTypes.ANCHOR:
        return this.renderAnchorInput();
      case LinkTypes.NONE:
        return this.renderNoLinkInput();
      default:
        return null;
    }
  }
  renderLinkSelection() {
    const {
      linkType,
      useEmailSubscription,
      allowFileDownload,
      allowMeetingLink,
      allowAnchorLinks,
      useNoLinkOption,
      dropdownClassName,
      allowEmailWebVersionLink
    } = this.props;
    const linkTypeOptions = [{
      text: I18n.text(i18nKey(`linkTypes.${LinkTypes.EMAIL}.option`)),
      value: LinkTypes.EMAIL
    }, {
      text: I18n.text(i18nKey(`linkTypes.${LinkTypes.URL}.option`)),
      value: LinkTypes.URL
    }, {
      text: I18n.text(i18nKey(`linkTypes.${LinkTypes.PAGE}.option`)),
      value: LinkTypes.PAGE
    }];
    if (allowEmailWebVersionLink) {
      linkTypeOptions.push({
        text: I18n.text(i18nKey(`linkTypes.${LinkTypes.VIEW_AS_WEBPAGE}.option`)),
        value: LinkTypes.VIEW_AS_WEBPAGE
      });
    }
    if (allowFileDownload) {
      linkTypeOptions.push({
        text: I18n.text(i18nKey(`linkTypes.${LinkTypes.FILE}.option`)),
        value: LinkTypes.FILE
      });
    }
    if (useEmailSubscription) {
      linkTypeOptions.push({
        text: I18n.text(i18nKey(`linkTypes.${LinkTypes.EMAIL_SUBSCRIPTION}.option`)),
        value: LinkTypes.EMAIL_SUBSCRIPTION
      });
    }
    if (allowMeetingLink) {
      linkTypeOptions.push({
        text: I18n.text(i18nKey(`linkTypes.${LinkTypes.MEETING}.option`)),
        value: LinkTypes.MEETING
      });
    }
    if (allowAnchorLinks) {
      linkTypeOptions.push({
        text: I18n.text(i18nKey(`linkTypes.${LinkTypes.ANCHOR}.option`)),
        value: LinkTypes.ANCHOR
      });
    }

    // re-order alphabetically based on option label
    linkTypeOptions.sort(alphabeticalSort);

    // place no-link at the top since it's the default zero-state
    if (useNoLinkOption) {
      linkTypeOptions.unshift({
        text: I18n.text(i18nKey(`linkTypes.${LinkTypes.NONE}.option`)),
        value: LinkTypes.NONE
      });
    }
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(UIFormControl, {
        className: "m-bottom-1",
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('linkTo')
        }),
        children: /*#__PURE__*/_jsx(UISelect, {
          dropdownClassName: dropdownClassName,
          menuStyle: {
            maxHeight: 300
          },
          value: linkType,
          placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('linkTypes.placeholder')
          }),
          options: linkTypeOptions,
          defaultValue: "url",
          searchable: false,
          "data-test-id": "link-type-select",
          onChange: this.handleLinkTypeChange,
          optionRenderer: option => /*#__PURE__*/_jsx(StyleProtectedSelectItem, {
            title: option.text,
            children: /*#__PURE__*/_jsx("span", {
              children: option.text
            })
          })
        })
      }), this.renderUrlField()]
    });
  }
  renderCheckboxes() {
    const {
      externalLink,
      linkType,
      showRefAndTargetControls,
      onUpdate,
      tooltipClassName
    } = this.props;
    if (showRefAndTargetControls) {
      const handleChange = ({
        target: {
          checked
        }
      }) => {
        onUpdate({
          externalLink: checked
        });
      };
      let readOnly = ReadOnlyLinkTypes.includes(linkType);
      const shouldBeExternal = ExternalLinkTypes.includes(linkType);
      if (readOnly && shouldBeExternal !== externalLink) {
        // This allow users to select the suggested option on legacy links
        readOnly = false;
      }
      return /*#__PURE__*/_jsx(UICheckbox, {
        className: "m-top-3",
        size: "small",
        checked: externalLink || linkType === LinkTypes.EMAIL,
        readOnly: readOnly,
        disabled: linkType === LinkTypes.NONE,
        onChange: handleChange,
        children: /*#__PURE__*/_jsx(UITooltip, {
          className: tooltipClassName,
          title: I18n.text(i18nKey('openNewWindowReadOnly'), {
            linkType
          }),
          placement: "top",
          disabled: !readOnly,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('openNewWindow')
          })
        })
      });
    }
    return null;
  }
  render() {
    return /*#__PURE__*/_jsxs("div", {
      children: [this.renderLinkSelection(), this.renderCheckboxes()]
    });
  }
}
LinkFields.propTypes = {
  externalLink: PropTypes.bool.isRequired,
  href: PropTypes.string.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onLinkTypeChange: PropTypes.func.isRequired,
  fetchPages: PropTypes.func.isRequired,
  fetchMeetingsByUsers: requiredBy('allowMeetingLink', PropTypes.func),
  fetchAnchors: requiredBy('allowAnchorLinks', PropTypes.func),
  linkType: PropTypes.oneOf(Object.values(LinkTypes)),
  onSelectFile: requiredBy('allowFileDownload', PropTypes.func),
  onUrlKeyDown: PropTypes.func.isRequired,
  currentFileMeta: PropTypes.object,
  dropdownClassName: PropTypes.string,
  showRefAndTargetControls: PropTypes.bool,
  useEmailSubscription: PropTypes.bool,
  allowFileDownload: PropTypes.bool,
  allowMeetingLink: PropTypes.bool,
  emailSubscriptionLinkTypes: PropTypes.array,
  allowAnchorLinks: PropTypes.bool,
  useNoLinkOption: PropTypes.bool,
  shouldAutoFocus: PropTypes.bool,
  userId: PropTypes.number,
  isValidLink: PropTypes.bool,
  allowEmailWebVersionLink: PropTypes.bool,
  tooltipClassName: PropTypes.string
};
LinkFields.defaultProps = {
  useEmailSubscription: false,
  allowFileDownload: true,
  allowAnchorLinks: false,
  useNoLinkOption: false,
  shouldAutoFocus: true,
  isValidLink: true,
  allowEmailWebVersionLink: false
};